import React from "react";
import ZipCodeInput from "./components/ZipCodeInput";
import PriceRangeInput from "./components/PriceRangeInput";
import PropertyTypeInput from "./components/PropertyTypeInput";
import BedBathInput from "./components/BedBathInput";
import FeedNameInput from "./components/FeedNameInput";
import FormHeader from "./components/FormHeader";
import FormNavigation from "./components/FormNavigation";

interface AddFeedFormProps {
    values: any;
    onChange: (field: string, value: any) => void;
    onReset: () => void;
    onSave: () => void;
    onBack: () => void;
    isEdit: boolean;
    step: number;
    onStepChange: (step: number) => void;
    onIncrement?: (field: string) => void;
    onDecrement?: (field: string) => void;
    onZipAdd?: () => void;
}

const AddFeedForm = ({ values, onChange, onReset, onSave, onBack, isEdit, step, onStepChange }: AddFeedFormProps) => {
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (step === 0) {
            onStepChange(1);
        } else {
            onSave();
        }
    };

    const handleBack = (e?: React.MouseEvent) => {
        e?.preventDefault();
        e?.stopPropagation();
        if (step === 0) {
            // If we're on the first step, go back to FeedList
            onBack();
        } else {
            // If we're on the second step, go back to first step
            onStepChange(0);
        }
    };

    const handleReset = () => {
        const currentName = values.name; // Store current name
        onReset(); // Reset all values
        onChange("name", currentName); // Restore the name
        onStepChange(0); // Always go back to first step when resetting
    };

    const handleNext = () => {
        onStepChange(1);
    };

    // Check if form is valid for the first step
    const isFirstStepValid = () => {
        // Check if there's at least one zipcode
        const hasZipcode = Array.isArray(values.zipcodes) && values.zipcodes.length > 0;

        // Get min and max price values, handling both formats
        const minPrice = values.priceMin || values.price?.min || "";
        const maxPrice = values.priceMax || values.price?.max || "";

        // Check if there are any validation errors in price range
        const hasPriceError =
            minPrice !== "" &&
            maxPrice !== "" &&
            Number(maxPrice.replace(/,/g, "")) < Number(minPrice.replace(/,/g, ""));

        // Check if there are any validation errors in bed/bath ranges
        const hasBedError =
            values.beds?.min !== "" && values.beds?.max !== "" && Number(values.beds.max) < Number(values.beds.min);

        const hasBathError =
            values.bath?.min !== "" && values.bath?.max !== "" && Number(values.bath.max) < Number(values.bath.min);

        // Only require zipcode if no fields are dirty
        const isFormDirty =
            minPrice !== "" ||
            maxPrice !== "" ||
            values.beds?.min !== "" ||
            values.beds?.max !== "" ||
            values.bath?.min !== "" ||
            values.bath?.max !== "";

        // If form is dirty, check for validation errors
        if (isFormDirty) {
            return hasZipcode && !hasPriceError && !hasBedError && !hasBathError;
        }

        // If form is not dirty, only require zipcode
        return hasZipcode;
    };

    return (
        <form
            className={`tw-flex tw-flex-col tw-h-full ${step === 1 ? "tw-min-h-[250px]" : "tw-min-h-[350px]"}`}
            onSubmit={handleSubmit}
        >
            <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
                {step === 0 && (
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <FormHeader onReset={handleReset} isEdit={isEdit} feedName={values.name} />
                        <ZipCodeInput
                            zipCodes={values.zipcodes || []}
                            onZipCodesChange={(zipcodes) => onChange("zipcodes", zipcodes)}
                        />
                        <PriceRangeInput values={values} onChange={onChange} />
                        <PropertyTypeInput value={values.type} onChange={(value) => onChange("type", value)} />
                        <BedBathInput
                            values={{
                                bedsMin: values.beds?.min || "",
                                bedsMax: values.beds?.max || "",
                                bathsMin: values.bath?.min || "",
                                bathsMax: values.bath?.max || "",
                            }}
                            onChange={(field, value) => {
                                // Map the field names back to the original structure
                                if (field === "bedsMin") onChange("beds", { ...values.beds, min: value });
                                else if (field === "bedsMax") onChange("beds", { ...values.beds, max: value });
                                else if (field === "bathsMin") onChange("bath", { ...values.bath, min: value });
                                else if (field === "bathsMax") onChange("bath", { ...values.bath, max: value });
                            }}
                        />
                    </div>
                )}
                {step === 1 && (
                    <div className="tw-flex tw-flex-col tw-gap-4">
                        <div className="tw-text-center tw-mb-4 tw-mt-2 tw-text-lg tw-font-semibold">Name your feed</div>
                        <FeedNameInput value={values.name} onChange={(value) => onChange("name", value)} />
                    </div>
                )}
            </div>
            <div className="tw-p-6 tw-bg-white tw-border-t tw-border-gray-100">
                <FormNavigation
                    onBack={handleBack}
                    submitText={step === 0 ? "Next" : "Done"}
                    onSubmit={step === 0 ? handleNext : onSave}
                    isFirstStep={step === 0}
                    disabled={step === 0 ? !isFirstStepValid() : !values.name.trim()}
                />
            </div>
        </form>
    );
};

export default AddFeedForm;
